<template>
    <div id="invoices">
        <AppBreadCrumb :paths="[{ title: $t('billing.invoices') }]"> </AppBreadCrumb>
        <a-card>
            <b-container class="mt-3" elavation="0" fluid>
                <md-progress-spinner v-if="loading" :md-diameter="20" class="md-accent" md-mode="indeterminate" />
                <div v-else>
                    <b-row class="filterContainer">
                        <b-col>
                            <b-form-group
                                id="start-date-input-group"
                                :label="$t('message.first')"
                                class="custom-input mb-0"
                                label-for="start-date-input"
                                label-sr-only
                            >
                                <datetime
                                    v-model="startDate"
                                    :max-datetime="maxDate"
                                    :min-datetime="lastFiveYear"
                                    :phrases="phrases"
                                    :placeholder="$t('message.startDate')"
                                    auto
                                    input-class="form-control rounded-0"
                                    input-id="start-date-input"
                                    type="datetime"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col class="pl-3">
                            <b-form-group
                                id="end-date-input-group"
                                :label="$t('message.last')"
                                class="custom-input mb-0"
                                label-for="end-date-input"
                                label-sr-only
                            >
                                <datetime
                                    v-model="endDate"
                                    :max-datetime="maxDate"
                                    :min-datetime="startDate"
                                    :phrases="phrases"
                                    :placeholder="$t('message.endDate')"
                                    auto
                                    input-class="form-control rounded-0"
                                    input-id="end-date-input"
                                    type="datetime"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <div class="ml-2 d-flex flex-column" style="width: 250px">
                                <strong style="font-size: 14px">{{ $t("message.siteAlias") }}</strong>
                                <b-form-group class="custom-input mb-0">
                                    <multiselect
                                        style="line-height: 17px !important; justify-content: center !important"
                                        v-model="siteId"
                                        :multiple="false"
                                        :options="sites"
                                        :placeholder="$t('billing.selectSiteAlias')"
                                        label="alias"
                                        track-by="_id"
                                        @search-change="autoCompleteForSites"
                                    />
                                </b-form-group>
                            </div>
                        </b-col>
                        <b-col>
                            <div class="ml-2 d-flex flex-column" style="width: 250px">
                                <strong>{{ $t("billing.priceIntervals") }}</strong>
                                <b-form-group class="custom-input mb-0">
                                    <multiselect
                                        v-model="priceIntervals"
                                        :hide-selected="true"
                                        :multiple="false"
                                        :options="PRICES"
                                        :placeholder="$t('billing.selectPriceInterval')"
                                        label="name"
                                        track-by="value"
                                    />
                                </b-form-group>
                            </div>
                        </b-col>
                        <b-col>
                            <div class="ml-2 d-flex flex-column mb-2">
                                <strong>{{ $t("billing.isPaid") }}</strong>
                                <a-radio-group v-model="paid" button-style="solid">
                                    <a-radio-button :value="null"> {{ $t("message.all") }}</a-radio-button>
                                    <a-radio-button :value="true">{{ $t("message.yes") }}</a-radio-button>
                                    <a-radio-button :value="false"> {{ $t("message.no") }}</a-radio-button>
                                </a-radio-group>
                            </div>
                        </b-col>
                        <b-col>
                            <div class="ml-2 d-flex flex-column mb-2">
                                <strong>{{ $t("billing.isRefunded") }}</strong>
                                <a-radio-group v-model="refund" button-style="solid">
                                    <a-radio-button :value="null"> {{ $t("message.all") }}</a-radio-button>
                                    <a-radio-button :value="true"> {{ $t("message.yes") }}</a-radio-button>
                                    <a-radio-button :value="false"> {{ $t("message.no") }}</a-radio-button>
                                </a-radio-group>
                            </div>
                        </b-col>
                    </b-row>

                    <a-table
                        :key="'vtable-' + renderCount"
                        :columns="cols()"
                        :dataSource="data"
                        :loading="invoiceRefreshing"
                        :locale="{
                            emptyText: $t('billing.noInvoiceFound'),
                        }"
                        :pagination="false"
                        bordered
                        class="custom-table2"
                        rowKey="_id"
                        @change="tableChange"
                    >
                        <div :key="'_idSlot'" :slot="'_idSlot'" slot-scope="data, item">
                            {{ item._id.slice(-8, -1) }}
                        </div>
                        <div :key="'siteIdSlot'" :slot="'siteIdSlot'" slot-scope="data, item">
                            {{ $_.get(item, "site.alias", "-") }}
                        </div>
                        <div :key="'priceSlot'" :slot="'priceSlot'" slot-scope="data, item">
                            {{ item.price.toFixed(2) }}
                        </div>
                        <div :key="'paidSlot'" :slot="'paidSlot'" slot-scope="data, item">
                            <md-switch v-model="item.paid" :class="item.paid ? 'md-primary' : ''" :disabled="true" />
                        </div>
                        <div :key="'createdDateSlot'" :slot="'createdDateSlot'" slot-scope="data, item">
                            {{ shortDate(item.createdDate) }}
                        </div>
                        <div :key="'paymentDateSlot'" :slot="'paymentDateSlot'" slot-scope="data, item">
                            {{ shortDate(item.paymentDate) }}
                        </div>
                        <div :key="'failDateSlot'" :slot="'failDateSlot'" slot-scope="data, item">
                            {{ shortDate(item.failDate) }}
                        </div>
                        <div :key="'refundSlot'" :slot="'refundSlot'" slot-scope="data, item">
                            <md-switch
                                v-model="item.refund"
                                :class="item.refund ? 'md-primary' : ''"
                                :disabled="true"
                            />
                        </div>
                    </a-table>
                    <div class="mt-3 d-flex justify-content-end">
                        <Pagination :page-change="pageChange" :page-sizes="pageSizes" :total="count" />
                    </div>
                </div>
            </b-container>
        </a-card>
    </div>
</template>

<script>
import { mapState } from "vuex";
import AppBreadCrumb from "../../components/common/AppBreadCrumb";
import moment from "moment";
import { getAllInvoicesPaginated } from "../../api/billing";
import Pagination from "../../components/Pagination";
import * as _ from "lodash";
import Multiselect from "vue-multiselect";
import log2 from "../../utils/log2";
import request from "../../utils/request";

const log = log2("Invoices");

const PRICES = [
    { value: "allForAfee", name: "All (For A Fee)" },
    { value: "all", name: "All" },
    { value: "0-100", name: "0-100" },
    { value: "100-300", name: "100-300" },
    { value: "300-500", name: "300-500" },
    { value: "500-750", name: "500-750" },
    { value: "750-1000", name: "750-1000" },
    { value: "1000+", name: "1000+" },
];
const SITE_ID = [{ _id: null, alias: "All" }];
const defaultStartDate = moment().startOf("day").toISOString();
const defaultEndDate = moment().endOf("day").toISOString();

export default {
    components: {
        AppBreadCrumb,
        Pagination,
        Multiselect,
    },
    computed: {
        ...mapState({
            loggedUser: (state) => state.auth.adminUser,
            isAdmin: (state) => state.auth.adminUser != null,
        }),
    },
    data() {
        return {
            cols: () => [
                {
                    dataIndex: "_id",
                    title: this.$t("billing.invoiceId"),
                    scopedSlots: { customRender: "_idSlot" },
                },
                {
                    dataIndex: "siteId",
                    title: this.$t("message.siteAlias"),
                    scopedSlots: { customRender: "siteIdSlot" },
                },
                {
                    dataIndex: "price",
                    title: this.$t("billing.price"),
                    scopedSlots: { customRender: "priceSlot" },
                },
                {
                    dataIndex: "paid",
                    title: this.$t("billing.isPaid"),
                    scopedSlots: { customRender: "paidSlot" },
                },
                {
                    dataIndex: "createdDate",
                    title: this.$t("billing.createdDate"),
                    scopedSlots: { customRender: "createdDateSlot" },
                },
                {
                    dataIndex: "paymentDate",
                    title: this.$t("billing.paymentDate"),
                    scopedSlots: { customRender: "paymentDateSlot" },
                },
                {
                    dataIndex: "failCount",
                    title: this.$t("billing.failCount"),
                    scopedSlots: { customRender: "failCountSlot" },
                },
                {
                    dataIndex: "failDate",
                    title: this.$t("billing.failDate"),
                    scopedSlots: { customRender: "failDateSlot" },
                },
                {
                    dataIndex: "refund",
                    title: this.$t("billing.isRefunded"),
                    scopedSlots: { customRender: "refundSlot" },
                },
            ],
            PRICES,
            SITE_ID,
            loading: true,
            data: [],
            count: 0,
            invoiceRefreshing: true,
            renderCount: 0,

            siteId: this.$route.query.siteId || SITE_ID,
            site_id: this.$route.query.site_id || null,
            paid: this.$route.query.paid || null,
            refund: this.$route.query.refund || null,
            page: this.$route.query.page || 1,
            perPage: this.$route.query.perPage || 10,
            startDate: this.$route.query.allTime
                ? null
                : this.$route.query.startDate
                ? moment(parseInt(this.$route.query.startDate)).toISOString()
                : defaultStartDate,
            endDate: this.$route.query.allTime
                ? null
                : this.$route.query.endDate
                ? moment(parseInt(this.$route.query.endDate)).toISOString()
                : defaultEndDate,
            maxDate: moment().toISOString(),
            lastFiveYear: moment().subtract(5, "year").toISOString(),
            phrases: {
                ok: this.$t("message.ok"),
                cancel: this.$t("message.cancel"),
            },

            pageSizes: ["10", "25", "50", "100"],
            invoices: {
                total: 0,
                count: 0,
                data: [],
            },
            priceIntervals:
                PRICES.find((p) => p.value === this.$route.query.prices) || PRICES.find((p) => p.value === "all"),

            sites: [],
        };
    },
    methods: {
        getInvoices(newSiteId = null) {
            this.invoiceRefreshing = true;

            if (this.isAdmin) {
                log("siteId in getInvoices before", this.site_id);
                this.site_id = newSiteId !== null ? newSiteId : this.siteId !== null ? this.siteId._id : this.site_id;
                log("siteId in getInvoices before2", this.site_id);
                const startDate = new Date(this.startDate).getTime();
                const endDate = new Date(this.endDate).getTime();
                this.updateQueryParams("page", this.page);
                this.updateQueryParams("perPage", this.perPage);
                this.updateQueryParams("Site_id", this.site_id);
                this.updateQueryParams("prices", this.priceIntervals.value);
                this.updateQueryParams("paid", this.paid);
                this.updateQueryParams("refund", this.refund);
                this.updateQueryParams("startDate", startDate ? startDate : null);
                this.updateQueryParams("endDate", endDate ? endDate : null);
                getAllInvoicesPaginated(
                    this.site_id,
                    this.priceIntervals.value,
                    this.paid,
                    this.refund,
                    this.perPage,
                    this.page,
                    this.startDate,
                    this.endDate
                )
                    .then((response) => {
                        if (response.data !== null) {
                            this.data = response.data.results;
                            this.count = response.data.count;
                            this.loading = false;
                            this.invoiceRefreshing = false;
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                    })
                    .finally(() => {
                        this.invoiceRefreshing = false;
                    });
                log("siteId in getInvoices after", this.site_id);
            }
        },
        updateQueryParams(key, value) {
            if (value === "" || value == null) {
                let query = Object.assign({}, this.$route.query);
                delete query[key];
                this.$router.replace({ query });
            } else {
                let { query } = this.$route;
                this.$router.replace({
                    query: { ...query, [key]: encodeURI(value) },
                });
            }
        },

        shortDate(date) {
            if (date == null) return "-";
            else return moment(date).format("YYYY.MM.DD");
        },
        tableChange(pagination, sorter) {
            log("tableChange", { pagination, sorter });
            this.perPage = pagination.pageSize;
            log("tableChange", { pagination, sorter });
            const newSort = {
                columnKey: sorter.columnKey,
                order: sorter.order,
            };
            const oldSort = this.currentSort;
            log("show me current Sort", {
                currentSort: this.currentSort,
                newSort,
            });
            if (!_.isEqual(newSort, oldSort)) {
                this.currentSort = newSort;
                this.renderCount += 1;
                this.getInvoices();
            }
        },
        pageChange(page, pageSize) {
            log("pageChange", { page, pageSize });
            this.page = page;
            this.perPage = pageSize;
        },
        autoCompleteForSites: _.debounce(async function (search = "") {
            let { data } = await request.post("sites/lookup", {
                search,
                pagination: {
                    pageSize: 10,
                    page: 1,
                },
            });
            this.sites = data.data;
            if (!this.sites.includes({ _id: null, alias: "All" })) {
                this.sites.unshift({ _id: null, alias: "All" });
                log("sites after autoComp", this.sites);
            }
            log("this.sites", this.sites);
        }, 500),
    },
    mounted() {
        log("siteId mounted:", this.siteId);
        log("site_id mounted:", this.site_id);
        this.priceIntervals.name =
            this.$route.query.prices === "allForAfee"
                ? "All (For A Fee)"
                : this.$route.query.prices === "all"
                ? "All"
                : this.$route.query.prices || "All";
        this.getInvoices();
        this.autoCompleteForSites();
    },
    beforeMount() {
        const isAccessEnabled =
            this.loggedUser.isFullAccess || this.loggedUser.approvedPages.some((page) => page.page === "invoices");
        if (!isAccessEnabled) {
            this.$router.push("/denied");
        }
    },
    watch: {
        startDate(newValue, oldValue) {
            if (true || newValue) {
                log("startDate updated", newValue);
                this.page = 1;
                this.getInvoices();
            }
        },
        endDate(newValue, oldValue) {
            if (true || newValue) {
                log("endDate updated", newValue);
                this.page = 1;
                this.getInvoices();
            }
        },
        page(newValue, oldValue) {
            if (newValue) {
                this.getInvoices();
            }
        },
        perPage(newValue, oldValue) {
            if (newValue) {
                this.getInvoices();
            }
        },
        siteId(newValue, oldValue) {
            log("siteId in watch", newValue);
            if (newValue) {
                let newSiteId = newValue._id;
                this.getInvoices(newSiteId);
            }
        },
        site_id(newValue, oldValue) {
            log("site_id in watch", newValue);
            if (newValue) {
                this.getInvoices();
            }
        },
        priceIntervals(newValue, oldValue) {
            if (newValue) {
                this.getInvoices();
            }
        },
        paid(newValue, oldValue) {
            if (newValue === null || newValue === true || newValue === false) {
                this.getInvoices();
            }
        },
        refund(newValue, oldValue) {
            if (newValue === null || newValue == true || newValue == false) {
                this.getInvoices();
            }
        },
    },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
#invoices {
    .ant-table-bordered .ant-table-thead > tr > th,
    .ant-table-bordered .ant-table-tbody > tr > td {
        text-align: center;
    }

    .ant-table-body .ant-table-column-title {
        color: #181c32;
        font-size: 15px !important;
        font-weight: 550 !important;
        font-family: Poppins;
        white-space: nowrap;
    }

    .filterContainer {
        padding: 5px;
        justify-content: center;
        align-items: center;
    }

    .col {
        max-width: max-content;
    }

    .ml-2,
    .mx-2 {
        text-align: center;
    }

    .filterContainer {
        padding: 14px;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .multiselect__tags {
        min-height: 30px !important;
        display: block;
        padding: 1px 36px 1px 7px !important;
        border-radius: 5px;
        border: 1px solid #e8e8e8;
        background: #fff;
        font-size: 14px;
        text-align: center !important;
    }

    .multiselect__select {
        position: absolute;
        min-width: 40px;
        height: 30px;
        right: 1px;
        top: 1px;
        padding: 0px 0px;
    }

    .multiselect,
    .multiselect__input,
    .multiselect__single {
        line-height: 29px !important;
        font-size: 15px;
    }

    .multiselect__single {
        padding-left: 5px;
        margin-bottom: 0px !important;
    }

    .multiselect__placeholder {
        color: #adadad;
        display: inline-block;
        margin: 5px !important;
        padding-top: 2px;
        font-size: 15px !important;
    }

    .ant-radio-group {
        line-height: 2 !important;
    }
}
</style>
